import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import {
  EXAM_TYPE_LABELS,
} from '../components/workers/WorkerModal';
import CertificadosAptitudModal from '../components/firmas/CertificadosAptitudModal';
import RegistrosConservados from '../components/firmas/RegistrosConservados';
import { firmasDisponibles } from '../lib/firmas/firmas-datos';
import ProgramaAnualPanel from '../components/programas/ProgramaAnualPanel';
import { programasDisponibles } from '../lib/programas/prog-datos';
import type {
  ExamType,
  MedicalAptitude,
  RiskLevel,
} from '../components/workers/WorkerModal';

// ── Periodicidad ───────────────────────────────────────────────────────────────

function calcularProximoExamen(
  lastExamDate: string | null,
  riskLevel: RiskLevel | null
): { proximaFecha: Date | null; mesesIntervalo: number } {
  const meses = riskLevel === 'alto' ? 6 : 12; // alto=6 meses, medio/bajo=12 meses (mínimo legal Ecuador)
  if (!lastExamDate) return { proximaFecha: null, mesesIntervalo: meses };
  const fecha = new Date(lastExamDate);
  fecha.setMonth(fecha.getMonth() + meses);
  return { proximaFecha: fecha, mesesIntervalo: meses };
}

type EstadoVigilancia = 'vencido' | 'por_vencer' | 'vigente' | 'sin_registro';

const ESTADO_COLORS: Record<EstadoVigilancia, string> = {
  vencido:      '#ef4444',
  por_vencer:   '#f59e0b',
  vigente:      '#22c55e',
  sin_registro: '#64748b',
};

const ESTADO_LABELS: Record<EstadoVigilancia, string> = {
  vencido:      'Vencido',
  por_vencer:   'Por vencer',
  vigente:      'Vigente',
  sin_registro: 'Sin registro',
};

const RISK_COLORS: Record<RiskLevel, string> = {
  bajo:  '#22c55e',
  medio: '#f59e0b',
  alto:  '#ef4444',
};

function calcularEstado(proximaFecha: Date | null): EstadoVigilancia {
  if (!proximaFecha) return 'sin_registro';
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const diffDias = Math.ceil((proximaFecha.getTime() - hoy.getTime()) / 86_400_000);
  if (diffDias < 0) return 'vencido';
  if (diffDias <= 30) return 'por_vencer';
  return 'vigente';
}

// ── Types ──────────────────────────────────────────────────────────────────────

interface WorkerHealthRow {
  id:                 string;
  full_name:          string;
  risk_level:         RiskLevel | null;
  last_medical_exam:  string | null;
  exam_type:          ExamType | null;
  medical_aptitude:   MedicalAptitude | null;
  proximaFecha:       Date | null;
  estado:             EstadoVigilancia;
}

interface IncidentAlertRow {
  id:            string;
  worker_id:     string;
  incident_date: string;
  incident_type: string;
  severity:      string;
  days_lost:     number;
}

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  onNavigateToWorkers?: () => void;
}

export default function HealthSurveillancePage({ onNavigateToWorkers }: Props = {}) {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const displayCompany   = impersonated ?? company;
  const companyId        = displayCompany?.id ?? '';
  const canEdit          = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const isAdmin          = profile?.role === 'admin' || !!impersonated;

  // Certificados de aptitud médica firmados (PO 4): solo con las tablas de firmas en la base.
  const [firmasOk,   setFirmasOk]   = useState(false);
  const [certTarget, setCertTarget] = useState<{ id: string; full_name: string } | null>(null);
  useEffect(() => { if (companyId) firmasDisponibles(companyId).then(setFirmasOk); }, [companyId]);
  // Cronograma de exámenes (PO 2): solo con las tablas de los programas anuales en la base.
  const [programasOk, setProgramasOk] = useState(false);
  useEffect(() => { if (companyId) programasDisponibles(companyId).then(setProgramasOk); }, [companyId]);

  const [rows,    setRows]    = useState<WorkerHealthRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [filterEstado, setFilterEstado] = useState<EstadoVigilancia | ''>('');
  const [filterRiesgo, setFilterRiesgo] = useState<RiskLevel | ''>('');
  const [filterSearch, setFilterSearch] = useState('');

  const [reintegroAlerts, setReintegroAlerts] = useState<Record<string, IncidentAlertRow>>({});

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);

    const [{ data: workerData }, { data: incData }] = await Promise.all([
      supabase
        .from('workers')
        .select('id, full_name, risk_level, last_medical_exam, exam_type, medical_aptitude')
        .eq('company_id', companyId)
        .eq('status', 'activo')
        .order('full_name'),
      supabase
        .from('incidents')
        .select('id, worker_id, incident_date, incident_type, severity, days_lost')
        .eq('company_id', companyId)
        .eq('medical_leave', true)
        .eq('status', 'cerrado')
        .not('worker_id', 'is', null),
    ]);

    const built: WorkerHealthRow[] = (workerData ?? []).map(w => {
      const { proximaFecha } = calcularProximoExamen(w.last_medical_exam, w.risk_level);
      return {
        id:                w.id,
        full_name:         w.full_name,
        risk_level:        w.risk_level,
        last_medical_exam: w.last_medical_exam,
        exam_type:         w.exam_type,
        medical_aptitude:  w.medical_aptitude,
        proximaFecha,
        estado:            calcularEstado(proximaFecha),
      };
    });
    setRows(built);

    // Build reintegro alerts: closed incidents with medical_leave=true, no subsequent reintegro exam
    const incidents = (incData ?? []) as IncidentAlertRow[];
    const workerIds = Array.from(new Set(incidents.map(i => i.worker_id)));

    if (workerIds.length > 0) {
      const { data: reintegroData } = await supabase
        .from('worker_medical_exams')
        .select('worker_id, exam_date')
        .in('worker_id', workerIds)
        .eq('exam_type', 'reintegro');

      const reintegroByWorker: Record<string, string[]> = {};
      (reintegroData ?? []).forEach((ex: any) => {
        if (!reintegroByWorker[ex.worker_id]) reintegroByWorker[ex.worker_id] = [];
        reintegroByWorker[ex.worker_id].push(ex.exam_date);
      });

      const alerts: Record<string, IncidentAlertRow> = {};
      incidents.forEach(inc => {
        const hasReintegro = (reintegroByWorker[inc.worker_id] ?? [])
          .some(d => d > inc.incident_date);
        if (!hasReintegro) {
          // Keep the most recent unresolved incident per worker
          if (!alerts[inc.worker_id] || inc.incident_date > alerts[inc.worker_id].incident_date) {
            alerts[inc.worker_id] = inc;
          }
        }
      });
      setReintegroAlerts(alerts);
    } else {
      setReintegroAlerts({});
    }

    setLoading(false);
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Métricas ──────────────────────────────────────────────────────────────────

  const totalActivos    = rows.length;
  const countVencido    = rows.filter(r => r.estado === 'vencido').length;
  const countPorVencer  = rows.filter(r => r.estado === 'por_vencer').length;
  const countSinRegistro = rows.filter(r => r.estado === 'sin_registro').length;
  const countVigentes   = rows.filter(r => r.estado === 'vigente').length;
  const coberturaPct    = totalActivos > 0 ? Math.round((countVigentes / totalActivos) * 100) : 0;

  // ── Filtros ───────────────────────────────────────────────────────────────────

  const filteredRows = rows.filter(r => {
    if (filterEstado && r.estado !== filterEstado) return false;
    if (filterRiesgo && r.risk_level !== filterRiesgo) return false;
    if (filterSearch && !r.full_name.toLowerCase().includes(filterSearch.toLowerCase())) return false;
    return true;
  });

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div>
        <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>
          Vigilancia de la Salud — Certificado de Aptitud Médica
        </h2>
        <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
          Periodicidad de exámenes médicos según nivel de riesgo: alto = cada 6 meses, medio/bajo = cada 12 meses (mínimo legal Ecuador)
        </p>
      </div>

      {/* Metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
        <MetricCard label="Trabajadores activos"      value={totalActivos}     color="#64748b" />
        <MetricCard label="Exámenes vencidos"          value={countVencido}     color={ESTADO_COLORS.vencido} />
        <MetricCard label="Por vencer (≤30 días)"      value={countPorVencer}   color={ESTADO_COLORS.por_vencer} />
        <MetricCard label="Sin registro"                value={countSinRegistro} color={ESTADO_COLORS.sin_registro} />
        <MetricCard label="Cobertura general"           value={`${coberturaPct}%`} color={ESTADO_COLORS.vigente} />
      </div>

      {programasOk && (
        <ProgramaAnualPanel tipo="examenes" companyId={companyId} empresa={displayCompany?.name ?? ''} puedeEditar={canEdit} />
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <select value={filterEstado} onChange={e => setFilterEstado(e.target.value as EstadoVigilancia | '')} style={{ ...sel, maxWidth: 200 }}>
          <option value="">Todos los estados</option>
          <option value="vencido">Vencido</option>
          <option value="por_vencer">Por vencer</option>
          <option value="vigente">Vigente</option>
          <option value="sin_registro">Sin registro</option>
        </select>
        <select value={filterRiesgo} onChange={e => setFilterRiesgo(e.target.value as RiskLevel | '')} style={{ ...sel, maxWidth: 180 }}>
          <option value="">Todos los riesgos</option>
          <option value="alto">Alto</option>
          <option value="medio">Medio</option>
          <option value="bajo">Bajo</option>
        </select>
        <input
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          placeholder="Buscar por nombre…"
          style={{ ...inp, maxWidth: 280, flex: 1 }}
        />
        {(filterEstado || filterRiesgo || filterSearch) && (
          <button onClick={() => { setFilterEstado(''); setFilterRiesgo(''); setFilterSearch(''); }} style={ghostBtn}>
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 14, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>Cargando…</div>
        ) : filteredRows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#475569', fontSize: '0.88rem' }}>
            {rows.length === 0 ? 'No hay trabajadores activos registrados.' : 'Ningún trabajador coincide con los filtros activos.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #334155' }}>
                  <th style={thStyle}>Trabajador</th>
                  <th style={thStyle}>Riesgo</th>
                  <th style={thStyle}>Último certificado</th>
                  <th style={thStyle}>Aptitud</th>
                  <th style={thStyle}>Próxima fecha</th>
                  <th style={thStyle}>Estado</th>
                  {firmasOk && <th style={thStyle}>Certificados</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => (
                  <tr key={r.id}
                    style={{ borderBottom: '1px solid rgba(51,65,85,0.5)', transition: 'background 0.1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.02)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                    <td style={td}>
                      <span style={{ color: '#e2e8f0', fontSize: '0.86rem' }}>{r.full_name}</span>
                      {reintegroAlerts[r.id] && (() => {
                        const alert = reintegroAlerts[r.id];
                        const dateStr = new Date(alert.incident_date + 'T00:00:00').toLocaleDateString('es-EC');
                        return (
                          <div style={{
                            marginTop: 6, padding: '6px 10px', borderRadius: 8,
                            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.4)',
                            display: 'flex', flexDirection: 'column', gap: 4,
                          }}>
                            <span style={{ color: '#fb923c', fontSize: '0.75rem', fontWeight: 700 }}>
                              ⚠️ Accidente con baja médica ({dateStr}) — pendiente examen de reintegro
                            </span>
                            {onNavigateToWorkers ? (
                              <button
                                onClick={onNavigateToWorkers}
                                style={{
                                  alignSelf: 'flex-start', background: 'none', border: '1px solid rgba(249,115,22,0.5)',
                                  borderRadius: 5, color: '#fb923c', cursor: 'pointer',
                                  fontSize: '0.72rem', padding: '2px 8px',
                                }}
                              >
                                Ir a Trabajadores →
                              </button>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                                Ve al módulo Trabajadores para registrar el examen
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={td}>
                      {r.risk_level ? (
                        <span style={{
                          background: `${RISK_COLORS[r.risk_level]}20`, color: RISK_COLORS[r.risk_level],
                          border: `1px solid ${RISK_COLORS[r.risk_level]}50`, borderRadius: 6,
                          padding: '3px 10px', fontSize: '0.74rem', fontWeight: 700, textTransform: 'capitalize',
                        }}>
                          {r.risk_level}
                        </span>
                      ) : <span style={{ color: '#475569' }}>—</span>}
                    </td>
                    <td style={td}>
                      {r.last_medical_exam ? (
                        <>
                          <span style={{ color: '#cbd5e1', fontSize: '0.84rem', display: 'block' }}>
                            {new Date(r.last_medical_exam + 'T00:00:00').toLocaleDateString('es-EC')}
                          </span>
                          <span style={{ color: '#64748b', fontSize: '0.74rem' }}>
                            {r.exam_type ? EXAM_TYPE_LABELS[r.exam_type] : '—'}
                          </span>
                        </>
                      ) : <span style={{ color: '#475569' }}>—</span>}
                    </td>
                    <td style={td}>
                      {(() => {
                        const aptStyle = r.medical_aptitude === 'apto'
                          ? { text: '✓ Apto', color: '#22c55e' }
                          : r.medical_aptitude === 'apto_con_restricciones'
                          ? { text: '⚠ Apto con restricciones', color: '#f59e0b' }
                          : r.medical_aptitude === 'no_apto'
                          ? { text: '✗ No apto', color: '#ef4444' }
                          : { text: 'Pendiente', color: '#64748b' };
                        return (
                          <span style={{
                            background: `${aptStyle.color}20`, color: aptStyle.color,
                            border: `1px solid ${aptStyle.color}50`, borderRadius: 8,
                            padding: '7px 16px', fontSize: '0.9rem', fontWeight: 700, whiteSpace: 'nowrap',
                            display: 'inline-block',
                          }}>
                            {aptStyle.text}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: '#94a3b8', fontSize: '0.8rem' }}>
                      {r.proximaFecha ? r.proximaFecha.toLocaleDateString('es-EC') : '—'}
                    </td>
                    <td style={td}>
                      <span style={{
                        background: `${ESTADO_COLORS[r.estado]}20`, color: ESTADO_COLORS[r.estado],
                        border: `1px solid ${ESTADO_COLORS[r.estado]}50`, borderRadius: 6,
                        padding: '4px 12px', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap',
                      }}>
                        {ESTADO_LABELS[r.estado]}
                      </span>
                    </td>
                    {firmasOk && (
                      <td style={td}>
                        <button type="button" onClick={() => setCertTarget({ id: r.id, full_name: r.full_name })} style={{ ...ghostBtn, minHeight: 34 }}
                          title="Certificados de aptitud firmados por el trabajador y el médico">
                          Certificados
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <p style={{ color: '#475569', fontSize: '0.78rem', textAlign: 'right', margin: 0 }}>
          {filteredRows.length} de {rows.length} trabajador{rows.length !== 1 ? 'es' : ''}
        </p>
      )}

      {firmasOk && (
        <RegistrosConservados companyId={companyId} tipo="aptitud_medica" usuarioId={profile?.id ?? null} puedeEditar={canEdit} esAdmin={isAdmin} recarga={rows} />
      )}

      {certTarget && (
        <CertificadosAptitudModal
          companyId={companyId}
          empresa={displayCompany?.name ?? ''}
          trabajador={certTarget}
          usuarioId={profile?.id ?? null}
          puedeEditar={canEdit}
          esAdmin={isAdmin}
          onCerrar={() => setCertTarget(null)}
        />
      )}

    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricCard({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div style={{ background: '#1e293b', border: `1px solid ${color}30`, borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ color, fontSize: '1.7rem', fontWeight: 800, lineHeight: 1 }}>{value}</span>
      <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1.3 }}>{label}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const thStyle:     React.CSSProperties = { padding: '11px 14px', textAlign: 'left', color: '#64748b', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const td:          React.CSSProperties = { padding: '11px 14px', verticalAlign: 'middle' };
const ghostBtn:    React.CSSProperties = { padding: '5px 12px', background: 'transparent', border: '1px solid #334155', color: '#94a3b8', borderRadius: 6, cursor: 'pointer', fontSize: '0.76rem', transition: 'all 0.15s' };
const inp:         React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'rgba(255,255,255,0.04)', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0', fontSize: '0.86rem', outline: 'none', boxSizing: 'border-box' };
const sel:         React.CSSProperties = { ...inp, cursor: 'pointer' };
